/**
 * Withdrawals — the balance, the request button, where the money should go, and
 * the history of everything requested so far.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { classNames } from '~/utils/classNames';
import {
  apiRequestPayout,
  apiSavePayoutDetails,
  type AffiliatePayout,
  type PayoutDetails,
  type PayoutMethod,
} from '~/lib/api/affiliates';
import {
  CARD,
  Cell,
  CellGrid,
  EmptyState,
  PAYOUT_TONE,
  Panel,
  PageHead,
  TextField,
  fmtDate,
  money,
  usePortal,
} from './shared';

const METHOD_LABEL: Record<PayoutMethod, string> = {
  bank_in: 'Bank transfer',
  upi: 'UPI',
};

const METHODS: { id: PayoutMethod; label: string }[] = [
  { id: 'upi', label: 'UPI' },
  { id: 'bank_in', label: 'Bank account' },
];

/** "•••• 4321" — never render a full account number back at the partner. */
function maskAccount(value: string): string {
  return `•••• ${value.slice(-4)}`;
}

function summarise(details: PayoutDetails): string {
  if (details.method === 'upi') {
    return details.upiId;
  }

  return `${details.accountName} · ${maskAccount(details.accountNumber)}`;
}

export default function Payouts() {
  const { data } = usePortal();
  const [editing, setEditing] = useState(false);

  const balance = data.balance;
  const details = data.payoutDetails ?? null;
  const payouts = data.payouts ?? [];
  const openPayout = payouts.find((p) => p.status === 'requested' || p.status === 'approved') ?? null;

  const available = balance?.availableCents ?? 0;
  const minimum = balance?.minPayoutCents ?? 1000;

  return (
    <div className="flex flex-col gap-7">
      <PageHead
        title="Payouts"
        subtitle={`Withdraw any amount from ${money(minimum)} up to your balance. We send payouts by hand, usually within a few working days.`}
      />

      <CellGrid columns={3}>
        <Cell label="Available" value={money(available)} accent note="Ready to withdraw" />
        <Cell label="In review" value={money(balance?.inReviewCents ?? 0)} note="In an open request" />
        <Cell label="Paid out" value={money(balance?.paidOutCents ?? 0)} />
      </CellGrid>

      <WithdrawCard available={available} minimum={minimum} hasDetails={!!details} openPayout={openPayout} />

      {/* Payout account */}
      <Panel
        title="Payout account"
        aside={
          <button
            type="button"
            className="text-[11.5px] text-secondary hover:text-primary touch:min-h-10 touch:px-2"
            onClick={() => setEditing((v) => !v)}
          >
            {editing ? 'Cancel' : details ? 'Edit' : 'Add details'}
          </button>
        }
      >
        <div className="px-5 py-4">
          {details ? (
            <>
              <div className="text-[13px] text-primary">{summarise(details)}</div>
              <div className="mt-0.5 text-[11.5px] text-muted">{METHOD_LABEL[details.method]}</div>
            </>
          ) : (
            <p className="text-[12.5px] text-muted">Not set yet. We can’t send money until you add an account.</p>
          )}

          {editing && <PayoutDetailsForm current={details} onDone={() => setEditing(false)} />}
        </div>
      </Panel>

      {/* History */}
      <Panel title="Withdrawal history">
        {payouts.length > 0 ? (
          <div className="divide-y divide-hairline">
            {payouts.map((p) => (
              <PayoutRow key={p.id} payout={p} />
            ))}
          </div>
        ) : (
          <EmptyState
            title="No withdrawals yet"
            body="Once you request one, every payout and its reference shows here."
          />
        )}
      </Panel>
    </div>
  );
}

/**
 * The withdrawal form. The amount is the partner's choice — defaulted to the
 * full balance because that is what most people want, but editable down to the
 * minimum so they can take part of it.
 */
function WithdrawCard({
  available,
  minimum,
  hasDetails,
  openPayout,
}: {
  available: number;
  minimum: number;
  hasDetails: boolean;
  openPayout: AffiliatePayout | null;
}) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');

  const request = useMutation({
    mutationFn: (cents: number) => apiRequestPayout(cents),
    onSuccess: () => {
      toast.success('Withdrawal requested — we’ll review it shortly.');
      setAmount('');
      qc.invalidateQueries({ queryKey: ['affiliate', 'me'] });
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message;
      const text = typeof message === 'string' ? message : 'Could not request a withdrawal.';
      setError(text);
      toast.error(text);
    },
  });

  if (openPayout) {
    return (
      <div className="ui-banner-warning">
        <span className="i-ph:clock shrink-0 text-[14px]" />
        <span>
          {money(openPayout.amountCents, openPayout.currency)} requested on {fmtDate(openPayout.requestedAt)} —{' '}
          {openPayout.status === 'approved' ? 'accepted, payment on its way.' : 'under review.'} You can request again
          once this one is settled.
        </span>
      </div>
    );
  }

  if (!hasDetails) {
    return (
      <div className={classNames(CARD, 'px-6 py-5')}>
        <div className="text-[13px] text-secondary">Withdraw your balance</div>
        <p className="mt-1.5 text-[12px] text-muted">Add your payout details below before requesting a withdrawal.</p>
      </div>
    );
  }

  if (available < minimum) {
    return (
      <div className={classNames(CARD, 'px-6 py-5')}>
        <div className="text-[13px] text-secondary">Withdraw your balance</div>
        <p className="mt-1.5 text-[12px] text-muted">
          You need {money(minimum)} to withdraw — you have {money(available)}. Keep sharing your link.
        </p>
      </div>
    );
  }

  /** Dollars in the input, cents on the wire — parsed once, here. */
  function parsed(): number | null {
    const value = Number(amount.replace(/[^0-9.]/g, ''));

    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }

    return Math.round(value * 100);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();

    const cents = parsed();

    if (cents === null) {
      setError('Enter an amount.');
      return;
    }

    if (cents < minimum) {
      setError(`The smallest withdrawal is ${money(minimum)}.`);
      return;
    }

    if (cents > available) {
      setError(`You can withdraw up to ${money(available)}.`);
      return;
    }

    setError('');
    request.mutate(cents);
  }

  return (
    <form className={classNames(CARD, 'px-4 py-5 sm:px-6')} onSubmit={submit} noValidate>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0 flex-1 sm:min-w-[220px]">
          <label className="ui-caption text-secondary" htmlFor="withdraw-amount">
            Amount to withdraw
          </label>

          <div
            className={classNames(
              'mt-2 flex items-center gap-1 rounded-lg border bg-sunken px-3 transition-colors focus-within:border-prominent',
              error ? 'border-danger/50' : 'border-hairline',
            )}
          >
            <span className="text-[15px] text-muted">$</span>
            <input
              id="withdraw-amount"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder={(available / 100).toFixed(2)}
              className="w-full bg-transparent py-2.5 font-geist text-[15px] tabular-nums text-primary outline-none placeholder:text-faint"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                setError('');
              }}
            />
            <button
              type="button"
              className="shrink-0 text-[11px] uppercase tracking-[0.12em] text-accent hover:brightness-110 touch:min-h-10 touch:px-2"
              onClick={() => {
                setAmount((available / 100).toFixed(2));
                setError('');
              }}
            >
              Max
            </button>
          </div>

          <p className={classNames('mt-2 text-[11.5px]', error ? 'text-danger' : 'text-muted')}>
            {error || `Between ${money(minimum)} and ${money(available)}.`}
          </p>
        </div>

        <button type="submit" className="ui-btn-accent shrink-0" disabled={request.isPending}>
          {request.isPending ? 'Requesting…' : 'Request withdrawal'}
        </button>
      </div>
    </form>
  );
}

function PayoutRow({ payout }: { payout: AffiliatePayout }) {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5 sm:px-5">
      <div className="min-w-0">
        <div className="text-[13px] text-primary">{money(payout.amountCents, payout.currency)}</div>
        {/* Wraps on phones — the payout reference and rejection note are the point of this row. */}
        <div className="mt-0.5 text-[11.5px] text-muted [overflow-wrap:anywhere] sm:truncate">
          Requested {fmtDate(payout.requestedAt)}
          {payout.paidAt && ` · paid ${fmtDate(payout.paidAt)}`}
          {payout.payoutRef && ` · ref ${payout.payoutRef}`}
          {payout.status === 'rejected' && payout.note && ` · ${payout.note}`}
        </div>
      </div>
      <span className={classNames('ui-pill shrink-0', PAYOUT_TONE[payout.status] ?? 'text-muted border-subtle')}>
        {payout.status}
      </span>
    </div>
  );
}

function PayoutDetailsForm({ current, onDone }: { current: PayoutDetails | null; onDone: () => void }) {
  const qc = useQueryClient();
  const [method, setMethod] = useState<PayoutMethod>(current?.method ?? 'upi');
  const [fields, setFields] = useState<Record<string, string>>(() => {
    if (!current) {
      return {};
    }

    const { method: _method, ...rest } = current as Record<string, unknown> & { method: PayoutMethod };

    return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, String(v ?? '')]));
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  function set(key: string, value: string) {
    setFields((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  }

  const save = useMutation({
    mutationFn: (details: PayoutDetails) => apiSavePayoutDetails(details),
    onSuccess: () => {
      toast.success('Payout details saved.');
      qc.invalidateQueries({ queryKey: ['affiliate', 'me'] });
      onDone();
    },
    onError: (err: any) => {
      const data = err?.response?.data?.message;

      if (data && typeof data === 'object') {
        // Nest returns zod fieldErrors — surface them on the fields themselves.
        setErrors(Object.fromEntries(Object.entries(data).map(([k, v]) => [k, String((v as string[])[0] ?? '')])));
        return;
      }

      toast.error(typeof data === 'string' ? data : 'Could not save your payout details.');
    },
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();

    const required = method === 'upi' ? ['upiId'] : ['accountName', 'accountNumber', 'ifsc'];
    const next: Record<string, string> = {};

    for (const key of required) {
      if (!fields[key]?.trim()) {
        next[key] = 'Required.';
      }
    }

    setErrors(next);

    if (Object.keys(next).length > 0) {
      return;
    }

    save.mutate({ method, ...fields } as unknown as PayoutDetails);
  }

  return (
    <form className="mt-5 border-t border-hairline pt-5" onSubmit={submit} noValidate>
      <div className="ui-segmented mb-5">
        {METHODS.map((m) => (
          <button
            key={m.id}
            type="button"
            className={classNames('ui-segmented-btn', method === m.id && 'ui-segmented-btn-active')}
            onClick={() => {
              setMethod(m.id);
              setErrors({});
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-4">
        {method === 'upi' ? (
          <TextField
            label="UPI ID"
            placeholder="yourname@okhdfcbank"
            value={fields.upiId ?? ''}
            error={errors.upiId}
            onChange={(v) => set('upiId', v)}
          />
        ) : (
          <>
            <TextField
              label="Account holder name"
              placeholder="As printed on the bank account"
              value={fields.accountName ?? ''}
              error={errors.accountName}
              onChange={(v) => set('accountName', v)}
            />
            <TextField
              label="Account number"
              placeholder="Your bank account number"
              value={fields.accountNumber ?? ''}
              error={errors.accountNumber}
              onChange={(v) => set('accountNumber', v)}
            />
            <TextField
              label="IFSC code"
              placeholder="HDFC0001234"
              value={fields.ifsc ?? ''}
              error={errors.ifsc}
              onChange={(v) => set('ifsc', v.toUpperCase())}
            />
            <TextField
              label="Bank name"
              optional
              placeholder="HDFC Bank"
              value={fields.bankName ?? ''}
              error={errors.bankName}
              onChange={(v) => set('bankName', v)}
            />
          </>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button type="submit" className="ui-btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save details'}
        </button>
        <p className="text-[11.5px] text-muted">Only our payouts team sees this.</p>
      </div>
    </form>
  );
}
